// __EQUIP_ASSIGN__ (2026-10-04) Equipment Assignment view - the pure part (no React, no database):
// which trailer a power unit pulls, who is on what, the pairing rules, and what a drop means.
//
// Henry: "We drag and drop units (compatible ones, so no truck to truck or trailer to trailer or
// gooseneck to straight truck) together under a driver's name." / "Isaac and Samuel share the
// same fleet. Isaac is part time so shares with others."
//
// Model (sql/equipment_assignments.sql):
//   - a driver has power units (trucks) in driver_equipment_assignments: ONE primary setup plus
//     shared / spare ones, each optionally limited to dates; a truck may be on many drivers;
//   - the trailer follows the truck it is coupled to: the truck's CURRENT combination
//     (truck_trailer_combinations, is_default first, newest first); one trailer per power unit
//     and one power unit per trailer.
// state = { drivers, trucks, trailers, combos, assignments } (rows as stored).
import { couplingCheck, unitDown } from './equipmentCompat';

export const truckName = (t) => (t ? t.truck_number || 'truck' : 'truck');
export const trailerName = (t) => (t ? t.trailer_number || 'trailer' : 'trailer');
const ts = (c) => (c && c.created_at ? Date.parse(c.created_at) || 0 : 0);

// ---------------------------------------------------------------- who / what
export function currentCombo(state, truckId) {
  const list = ((state && state.combos) || []).filter((c) => c.truck_id === truckId);
  if (!list.length) return null;
  return [...list].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0) || ts(b) - ts(a))[0];
}
export const couplingOf = (state, truckId) => { const c = currentCombo(state, truckId); return c ? c.trailer_id || null : null; };
export function pulledBy(state, trailerId) {
  if (!trailerId) return null;
  const c = ((state && state.combos) || []).find((x) => x.trailer_id === trailerId && x.truck_id && currentCombo(state, x.truck_id) === x);
  return c ? c.truck_id : null;
}
export const assignmentsOf = (state, driverId) => ((state && state.assignments) || []).filter((a) => a.driver_id === driverId);
export const driversOnTruck = (state, truckId) => ((state && state.assignments) || []).filter((a) => a.truck_id === truckId)
  .map((a) => ({ assignment: a, driver: ((state && state.drivers) || []).find((d) => d.id === a.driver_id) || null }))
  .filter((x) => x.driver);

// A driver's lane: { primary: {assignment, truck, trailer} | null, shared: [...] }
export function laneOf(state, driverId) {
  const truckById = (id) => ((state && state.trucks) || []).find((t) => t.id === id) || null;
  const trailerById = (id) => ((state && state.trailers) || []).find((t) => t.id === id) || null;
  const rows = assignmentsOf(state, driverId).map((a) => {
    const truck = truckById(a.truck_id);
    return { assignment: a, truck, trailer: truck ? trailerById(couplingOf(state, truck.id)) : null };
  }).filter((x) => x.truck)
    .sort((x, y) => truckName(x.truck).localeCompare(truckName(y.truck)));
  return { primary: rows.find((x) => x.assignment.is_primary) || null, shared: rows.filter((x) => !x.assignment.is_primary) };
}

// ---------------------------------------------------------------- pairing rules
// __EQUIP_R5__ the hitch rules live in utils/equipmentCompat.js (twin: equipment_compat.py, shared
// coupling_cases in the cases file); re-exported here for the view.
export { HITCH_LABEL, trailerHitch, trailerHitches, truckHitches, isStraightTruck, couplingCheck, unitDown } from './equipmentCompat';

// ---------------------------------------------------------------- what a drop means
// item   : { kind: 'truck' | 'trailer', id, from: { type: 'pool' } | { type: 'lane', driverId, slot }
//            | { type: 'coupled', truckId, driverId } }
// target : { type: 'pool' } | { type: 'slot', driverId, slot: 'primary' | 'shared' }
//            | { type: 'truck', truckId, driverId? } | { type: 'trailer', trailerId }
// -> { ok, noop?, reason?, warn?, ops: [equipment_assign ops], summary }
export function dropCheck(state, item, target) {
  const S = state || {};
  const no = (reason) => ({ ok: false, reason, ops: [] });
  const noop = (reason) => ({ ok: false, noop: true, reason, ops: [] });
  if (!item || !target) return noop('');
  const truckById = (id) => (S.trucks || []).find((t) => t.id === id) || null;
  const trailerById = (id) => (S.trailers || []).find((t) => t.id === id) || null;
  const driverName = (id) => { const d = (S.drivers || []).find((x) => x.id === id); return d ? d.name || 'driver' : 'driver'; };
  const from = item.from || { type: 'pool' };

  if (target.type === 'pool') {
    if (item.kind === 'truck' && from.type === 'lane') {
      const t = truckById(item.id);
      return { ok: true, ops: [{ op: 'unassign', driver_id: from.driverId, truck_id: item.id }], summary: truckName(t) + ' taken off ' + driverName(from.driverId) };
    }
    if (item.kind === 'trailer' && from.type === 'coupled') {
      return { ok: true, ops: [{ op: 'uncouple', truck_id: from.truckId }], summary: trailerName(trailerById(item.id)) + ' unhitched from ' + truckName(truckById(from.truckId)) };
    }
    return noop('Already in the pool');
  }

  if (target.type === 'trailer') {
    return no(item.kind === 'trailer' ? 'Trailer to trailer does not pair - drop a trailer on a power unit.'
      : 'Drag the trailer onto the power unit to hitch them.');
  }

  if (target.type === 'truck') {
    const truck = truckById(target.truckId);
    if (item.kind === 'truck') {
      return no(item.id === target.truckId ? '' : 'Truck to truck does not pair - drop a trailer on a power unit, or a power unit on a driver.');
    }
    const trailer = trailerById(item.id);
    const cur = couplingOf(S, target.truckId);
    if (cur === item.id) return noop(truckName(truck) + ' already pulls ' + trailerName(trailer));
    if (cur) {
      const other = trailerName(trailerById(cur));
      return no(truckName(truck) + ' already pulls ' + other + ' - drag ' + other + ' off first (one trailer per power unit).');
    }
    const cc = couplingCheck(truck, trailer);
    if (!cc.ok) return no(cc.reason);
    const was = pulledBy(S, item.id);
    return {
      ok: true, warn: cc.warn, ops: [{ op: 'couple', truck_id: target.truckId, trailer_id: item.id }],
      summary: trailerName(trailer) + ' hitched to ' + truckName(truck) + (was && was !== target.truckId ? ' (taken off ' + truckName(truckById(was)) + ')' : ''),
    };
  }

  if (target.type === 'slot') {
    const dId = target.driverId;
    const lane = laneOf(S, dId);
    if (item.kind === 'trailer') {
      if (target.slot !== 'primary' || !lane.primary) {
        return no(lane.primary ? 'Drop a trailer on the power unit it should hitch to.'
          : 'Give ' + driverName(dId) + ' a power unit first - a trailer is pulled by a truck.');
      }
      return dropCheck(S, item, { type: 'truck', truckId: lane.primary.truck.id, driverId: dId });
    }
    const truck = truckById(item.id);
    if (!truck) return no('That unit no longer exists.');
    const mine = assignmentsOf(S, dId).find((a) => a.truck_id === item.id);
    const wantPrimary = target.slot === 'primary';
    const dn = unitDown(truck);
    const warn = dn ? truckName(truck) + ' is ' + dn : null;
    if (mine && !!mine.is_primary === wantPrimary) return noop(truckName(truck) + ' is already ' + driverName(dId) + "'s " + (wantPrimary ? 'primary' : 'shared') + ' unit');
    const ops = [];
    if (from.type === 'lane' && from.driverId !== dId) ops.push({ op: 'unassign', driver_id: from.driverId, truck_id: item.id });
    if (mine && wantPrimary) ops.push({ op: 'set_primary', driver_id: dId, truck_id: item.id });
    else ops.push({ op: 'assign', driver_id: dId, truck_id: item.id, primary: wantPrimary });
    const replaced = wantPrimary && lane.primary && lane.primary.truck.id !== item.id ? lane.primary.truck : null;
    const others = driversOnTruck(S, item.id).filter((x) => x.driver.id !== dId && !(from.type === 'lane' && x.driver.id === from.driverId));
    let summary = truckName(truck) + ' -> ' + driverName(dId) + (wantPrimary ? ' (primary)' : ' (shared)');
    if (from.type === 'lane' && from.driverId !== dId) summary += ', moved from ' + driverName(from.driverId);
    if (replaced) summary += mine ? '; ' + truckName(replaced) + ' becomes shared' : '; ' + truckName(replaced) + ' back to the pool';
    if (others.length) summary += '; also on ' + others.map((x) => x.driver.name).join(', ');
    return { ok: true, warn, ops: ops.length === 1 ? ops : [{ op: 'batch', ops }], summary };
  }
  return noop('');
}

// ---------------------------------------------------------------- undo
// The restore op that puts back what `ops` will change: the drivers' rows and the couplings of
// every truck involved (and of the truck that pulls a trailer being moved).
export function undoFor(state, ops) {
  const flat = [];
  const walk = (o) => { if (!o) return; if (o.op === 'batch') (o.ops || []).forEach(walk); else flat.push(o); };
  (ops || []).forEach(walk);
  const drivers = {}; const trucks = new Set();
  flat.forEach((o) => {
    if (o.driver_id) drivers[o.driver_id] = assignmentsOf(state, o.driver_id).map((a) => ({
      truck_id: a.truck_id, is_primary: !!a.is_primary, start_date: a.start_date || null, end_date: a.end_date || null, note: a.note || null,
    }));
    if (o.op === 'couple' || o.op === 'uncouple') {
      trucks.add(o.truck_id);
      if (o.trailer_id) { const was = pulledBy(state, o.trailer_id); if (was) trucks.add(was); }
    }
  });
  return { op: 'restore', drivers, couplings: [...trucks].map((t) => ({ truck_id: t, trailer_id: couplingOf(state, t) })) };
}

// ---------------------------------------------------------------- local (optimistic) apply
// Mirrors equipment_assign() closely enough for the screen; the view re-reads the fleet after
// every saved change.
export function applyOps(state, ops) {
  let S = { ...state, assignments: [...(state.assignments || [])], combos: [...(state.combos || [])] };
  const setCoupling = (truckId, trailerId) => {
    if (trailerId) {
      S.combos = S.combos.map((c) => (c.trailer_id === trailerId && c.truck_id !== truckId && currentCombo(S, c.truck_id) === c ? { ...c, trailer_id: null } : c));
    }
    const cur = currentCombo(S, truckId);
    if (cur) S.combos = S.combos.map((c) => (c === cur ? { ...c, trailer_id: trailerId || null } : c));
    else S.combos = [...S.combos, { id: 'local-' + truckId, truck_id: truckId, trailer_id: trailerId || null, is_default: true, combination_name: '' }];
  };
  const one = (o) => {
    if (o.op === 'batch') { (o.ops || []).forEach(one); return; }
    if (o.op === 'assign') {
      if (o.primary) S.assignments = S.assignments.filter((a) => !(a.driver_id === o.driver_id && a.is_primary && a.truck_id !== o.truck_id));
      const ex = S.assignments.find((a) => a.driver_id === o.driver_id && a.truck_id === o.truck_id);
      const patch = {};
      if ('primary' in o) patch.is_primary = !!o.primary;
      ['start_date', 'end_date', 'note'].forEach((k) => { if (k in o) patch[k] = o[k] || null; });
      S.assignments = ex ? S.assignments.map((a) => (a === ex ? { ...a, ...patch } : a))
        : [...S.assignments, { id: 'local-' + o.driver_id + o.truck_id, driver_id: o.driver_id, truck_id: o.truck_id, is_primary: !!o.primary, start_date: o.start_date || null, end_date: o.end_date || null, note: o.note || null }];
    } else if (o.op === 'unassign') {
      S.assignments = S.assignments.filter((a) => !(a.driver_id === o.driver_id && a.truck_id === o.truck_id));
    } else if (o.op === 'set_primary') {
      S.assignments = S.assignments.map((a) => (a.driver_id === o.driver_id ? { ...a, is_primary: a.truck_id === o.truck_id } : a));
    } else if (o.op === 'couple') {
      setCoupling(o.truck_id, o.trailer_id);
    } else if (o.op === 'uncouple') {
      setCoupling(o.truck_id, null);
    } else if (o.op === 'restore') {
      Object.keys(o.drivers || {}).forEach((d) => {
        S.assignments = S.assignments.filter((a) => a.driver_id !== d)
          .concat((o.drivers[d] || []).map((r) => ({ id: 'local-' + d + r.truck_id, driver_id: d, ...r })));
      });
      (o.couplings || []).filter((c) => !c.trailer_id).forEach((c) => setCoupling(c.truck_id, null));
      (o.couplings || []).filter((c) => c.trailer_id).forEach((c) => setCoupling(c.truck_id, c.trailer_id));
    }
  };
  (ops || []).forEach(one);
  return S;
}
